import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, Card, Accordion, Alert } from 'react-bootstrap';
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaClock } from 'react-icons/fa';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [validated, setValidated] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const form = e.currentTarget;

    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    // In a real app, you would send the data to your backend here
    console.log('Form submitted:', formData);

    // Show success message and reset form
    setSubmitSuccess(true);
    setFormData({
      name: '',
      email: '',
      subject: '',
      message: ''
    });
    setValidated(false);

    // Hide success message after 5 seconds
    setTimeout(() => {
      setSubmitSuccess(false);
    }, 5000);
  };

  const faqs = [
    {
      question: "What are your shipping rates?",
      answer: "We offer free standard shipping on all orders over ₹50000. For orders under ₹50000, shipping costs are calculated based on the weight and destination of your package."
    },
    {
      question: "How long does shipping take?",
      answer: "Standard shipping typically takes 3-5 business days. Express shipping options are available at checkout for faster delivery."
    },
    {
      question: "What is your return policy?",
      answer: "We accept returns within 30 days of purchase. Items must be in their original condition with tags attached. Please use our return portal or contact customer service to initiate a return."
    },
    {
      question: "Do you ship internationally?",
      answer: "Yes, we ship to select countries worldwide. International shipping rates and delivery times vary by location."
    },
    {
      question: "How can I track my order?",
      answer: "Once your order ships, you'll receive a tracking number via email. You can also track your order by logging into your account on our website."
    }
  ];

  return (
    <Container className="py-5">
      <Row className="mb-1">
        <Col lg={12}>
          <h1 className="text-center mb-4">Contact Us</h1>
          <p className="text-center lead mb-5">
            We're here to help! Reach out to us with any questions, concerns, or feedback.
          </p>
        </Col>
      </Row>

      <Row className="mb-5">
        {/* Contact Form */}
        <Col lg={7} className="mb-4 mb-lg-0">
          <Card className="border-0" style={{
            boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
            borderRadius: 24,
            background: '#fff',
            padding: 0,
          }}>
            <Card.Body className="p-4" style={{ borderRadius: 24, padding: '2.5rem 2rem' }}>
              <h3 className="mb-4">Send Us a Message</h3>

              {submitSuccess && (
                <Alert variant="success" onClose={() => setSubmitSuccess(false)} dismissible>
                  Your message has been sent successfully! We'll get back to you soon.
                </Alert>
              )}

              <Form noValidate validated={validated} onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Your Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        style={{ borderRadius: 12, background: '#f8fafc', border: '1.5px solid #e0e7ef' }}
                      />
                      <Form.Control.Feedback type="invalid">
                        Please provide your name.
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Email Address</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        style={{ borderRadius: 12, background: '#f8fafc', border: '1.5px solid #e0e7ef' }}
                      />
                      <Form.Control.Feedback type="invalid">
                        Please provide a valid email.
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Subject</Form.Label>
                  <Form.Control
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    style={{ borderRadius: 12, background: '#f8fafc', border: '1.5px solid #e0e7ef' }}
                  />
                  <Form.Control.Feedback type="invalid">
                    Please provide a subject.
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>Your Message</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    style={{ borderRadius: 12, background: '#f8fafc', border: '1.5px solid #e0e7ef' }}
                  />
                  <Form.Control.Feedback type="invalid">
                    Please enter your message.
                  </Form.Control.Feedback>
                </Form.Group>

                <Button variant="primary" type="submit" size="lg" style={{ borderRadius: 16, padding: '10px 36px', fontWeight: 600, fontSize: 18 }}>
                  Send Message
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        {/* Contact Information */}
        <Col lg={5}>
          <Card className="border-0 mb-4" style={{
            boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
            borderRadius: 24,
            background: '#fff',
            padding: 0,
            height: '100%'
          }}>
            <Card.Body className="p-4" style={{ borderRadius: 24, padding: '2.5rem 2rem' }}>
              <h3 className="mb-4">Contact Information</h3>

              <div className="d-flex mb-4">
                <div className="me-3">
                  <FaMapMarkerAlt size={24} className="text-primary" />
                </div>
                <div>
                  <h5 className="mb-1">Our Location</h5>
                  <p className="mb-0">Plot No. Y 9 & 10, 2nd Floor, Vipin Garden,
                  Main Najafgarh Road, Near Metro Pillar No. 801, ND-59</p>
                </div>
              </div>

              <div className="d-flex mb-4">
                <div className="me-3">
                  <FaPhone size={24} className="text-primary" />
                </div>
                <div>
                  <h5 className="mb-1">Phone</h5>
                  <p className="mb-0">+91-9876543210</p>
                </div>
              </div>

              <div className="d-flex mb-4">
                <div className="me-3">
                  <FaEnvelope size={24} className="text-primary" />
                </div>
                <div>
                  <h5 className="mb-1">Email</h5>
                  <p className="mb-0">contact@teleringprocess.com</p>
                </div>
              </div>

              <div className="d-flex">
                <div className="me-3">
                  <FaClock size={24} className="text-primary" />
                </div>
                <div>
                  <h5 className="mb-1">Business Hours</h5>
                  <p className="mb-0">
                    Monday - Friday: 9:00 AM - 5:00 PM<br />
                    Saturday: 10:00 AM - 3:00 PM<br />
                    Sunday: Closed
                  </p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* FAQ Section */}
      <Row className="mb-5">
        <Col lg={12}>
          <h3 className="text-center mb-4">Frequently Asked Questions</h3>

          <Accordion defaultActiveKey="0" className="shadow-sm">
            {faqs.map((faq, index) => (
              <Accordion.Item eventKey={index.toString()} key={index}>
                <Accordion.Header>{faq.question}</Accordion.Header>
                <Accordion.Body>
                  {faq.answer}
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Col>
      </Row>

      {/* Map Section */}
      <Row>
        <Col lg={12} className="mb-3">
          <h3 className="text-center mb-4">Find Us</h3>
        </Col>
        <Col lg={12} className="d-flex justify-content-center">
          <a
            href="https://www.google.com/maps/search/?api=1&query=Plot+No.+Y+9+%26+10,+Vipin+Garden,+Main+Najafgarh+Road,+Near+Metro+Pillar+No.+801,+ND-59"
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: 'inline-block' , width: '100%'}}
          >
            <img
              src="https://maps.googleapis.com/maps/api/staticmap?center=Plot%20No.%20Y%209%20%26%2010,%20Vipin%20Garden,%20Main%20Najafgarh%20Road,%20Near%20Metro%20Pillar%20No.%20801,%20ND-59&zoom=16&size=600x400&markers=color:red%7Clabel:P%7CPlot%20No.%20Y%209%20%26%2010,%20Vipin%20Garden,%20Main%20Najafgarh%20Road,%20Near%20Metro%20Pillar%20No.%20801,%20ND-59&key=AIzaSyCtuLzudGcL1XI9XrpzvQzOXCqvug5bgRs"
              alt="Map showing location of Telering Process Pvt. Ltd."
              style={{ width: '100%', height: 400, borderRadius: 18, boxShadow: '0 4px 24px rgba(31,38,135,0.10)', cursor: 'pointer' }}
            />
          </a>
        </Col>
      </Row>
    </Container>
  );
};

export default ContactPage; 